// src/components/Map/RouteSimulator.tsx
// VERSION FINALE ULTIME – TOUT MARCHE PARFAITEMENT

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Marker, useMap } from "react-leaflet";
import L, { DivIcon, LatLngExpression } from "leaflet";

interface RouteSimulatorProps {
  path: [number, number][];
  pointSpeeds?: number[];
  speed?: number;
  playing?: boolean;
  icon?: DivIcon;
  autofit?: boolean;
  right?: boolean;
  iconSize?: number;
}

const RouteSimulator: React.FC<RouteSimulatorProps> = ({
  path,
  pointSpeeds = [],
  speed = 100,
  playing = false,
  icon,
  autofit = false,
  right = true,
  iconSize = 30,
}) => {
  const map = useMap();
  const [index, setIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(speed);
  const [playingState, setPlayingState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isPlaying = useRef(false);
  const restartAfterSpeedChangeRef = useRef(false);

  // Icône rouge pulsante magnifique
  const pulseIcon = useMemo(() => {
    const html = `
      <div style="position:relative;width:${iconSize}px;height:${iconSize}px;">
        <div style="
          background:#ff3b30;
          border:3px solid #ff8a80;
          border-radius:50%;
          width:100%;height:100%;
          box-shadow:0 0 14px rgba(255,59,48,0.65);
          animation:pulse 2.2s infinite;
        "></div>
        <div style="
          position:absolute;
          inset:-8px;
          border:2px solid rgba(255,59,48,0.65);
          border-radius:50%;
          animation:ripple 2.2s infinite;
          opacity:0;
        "></div>
      </div>
      <style>
        @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}
        @keyframes ripple{0%{transform:scale(0.82);opacity:0.55}100%{transform:scale(2.05);opacity:0}}
      </style>
    `;

    return L.divIcon({
      html,
      className: "route-simulator-pulse",
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2],
    });
  }, [iconSize]);

  const effectiveIcon = icon ?? pulseIcon;

  const routeDistances = useMemo(() => {
    const cumulativeDistances = [0];
    let total = 0;

    for (let i = 1; i < path.length; i += 1) {
      total += map.distance(path[i - 1], path[i]) / 1000;
      cumulativeDistances.push(total);
    }

    return { cumulativeDistances, total };
  }, [map, path]);

  const formatDistance = (value: number) => {
    if (value < 1) return `${Math.round(value * 1000)} m`;
    return `${value.toFixed(value >= 10 ? 0 : 1)} km`;
  };

  const currentPointSpeed = useMemo(() => {
    const speedValue = Number(pointSpeeds[index] ?? 0);
    return Number.isFinite(speedValue) && speedValue >= 0
      ? Math.round(speedValue)
      : 0;
  }, [index, pointSpeeds]);

  // Contrôles
  const stop = () => {
    isPlaying.current = false;
    setPlayingState(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = () => {
    if (path.length < 2 || isPlaying.current) return;
    isPlaying.current = true;
    setPlayingState(true);
    if (timerRef.current) clearInterval(timerRef.current);
    const interval = Math.max(280, 2400 / Math.max(playbackSpeed, 10) * 10);
    timerRef.current = setInterval(() => {
      setIndex((i) => {
        if (i + 1 >= path.length) {
          stop();
          return path.length - 1;
        }
        return i + 1;
      });
    }, interval);
  };

  const reset = () => {
    stop();
    setIndex(0);
    if (autofit && path.length > 1) {
      map.fitBounds(L.polyline(path).getBounds(), { padding: [60, 60] });
    }
  };

  const changeSpeed = (delta: number) => {
    const wasPlaying = isPlaying.current;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    isPlaying.current = false;
    setPlayingState(false);

    setPlaybackSpeed((currentSpeed) =>
      Math.min(200, Math.max(10, currentSpeed + delta))
    );

    restartAfterSpeedChangeRef.current = wasPlaying;
  };

  // Play/Pause externe
  useEffect(() => {
    playing ? start() : stop();
  }, [playing]);

  useEffect(() => {
    if (!restartAfterSpeedChangeRef.current) return;
    restartAfterSpeedChangeRef.current = false;
    start();
  }, [playbackSpeed]);

  useEffect(() => {
    setPlaybackSpeed(speed);
  }, [speed]);

  // Reset quand le path change
  useEffect(() => {
    reset();
    if (playing) {
      window.setTimeout(start, 0);
    }
  }, [path]);

  // MANIPULATION À LA ROULETTE (LE FEU PUR)
  useEffect(() => {
    if (path.length === 0) return;

    const container = map.getContainer();

    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();
      stop(); // pause auto dès que tu touches

      setIndex((i) => {
        const step = e.deltaY > 0 ? 1 : -1;
        const next = i + step;
        if (next < 0) return 0;
        if (next >= path.length) return path.length - 1;
        return next;
      });
    };

    container.addEventListener("wheel", onWheel, { passive: false });

    // Désactiver le zoom quand Shift enfoncé
    const disableZoom = () => map.scrollWheelZoom.disable();
    const enableZoom = () => map.scrollWheelZoom.enable();

    window.addEventListener("keydown", (e) => e.key === "Shift" && disableZoom());
    window.addEventListener("keyup", (e) => e.key === "Shift" && enableZoom());

    return () => {
      container.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", disableZoom);
      window.removeEventListener("keyup", enableZoom);
      map.scrollWheelZoom.enable();
    };
  }, [map, path.length]);

  // Nettoyage final
    // Nettoyage parfait – zéro warning, zéro erreur
  useEffect(() => {
    return () => {
      timerRef.current && clearInterval(timerRef.current);
    };
  }, []);
  if (path.length === 0) return null;

  return (
    <>
      {/* Marqueur animé */}
      <Marker position={path[index]} icon={effectiveIcon} zIndexOffset={2000} />

      {/* Contrôleur moderne + indication molette */}
      <div
        className="route-simulator-control"
        style={{
          position: "absolute",
          top: 12,
          [right ? "right" : "left"]: right ? 86 : 12,
          zIndex: 1000,
          background: "rgba(0,0,0,0.82)",
          color: "#fff",
          padding: "10px 16px",
          borderRadius: "14px",
          fontSize: "13px",
          fontWeight: "600",
          backdropFilter: "blur(12px)",
          boxShadow: "0 6px 25px rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div className="route-simulator-help" style={{ opacity: 0.9 }}>
          <i className="fas fa-hand-pointer"></i> Shift + molette = contrôle
        </div>

        <div className="route-simulator-metrics" style={{ borderLeft: "1px solid rgba(255,255,255,0.3)", paddingLeft: "12px" }}>
          <i className="fas fa-tachometer-alt"></i> {currentPointSpeed} km/h
          <span className="mx-2">/</span>
          <i className="fas fa-route"></i>{" "}
          {formatDistance(routeDistances.cumulativeDistances[index] || 0)}
          <span className="mx-2">•</span>
          {index + 1} / {path.length}
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="route-simulator-speed-btn"
            onClick={() => changeSpeed(-10)}
            style={{
              background: "#3a3a3c",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "17px",
            }}
          >
            <i className="fas fa-minus"></i>
          </button>

          <button
            className={`route-simulator-play-btn ${playingState ? "is-playing" : ""}`}
            onClick={() => (isPlaying.current ? stop() : start())}
            style={{
              background: playingState ? "#ff3b30" : "#34c759",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "17px",
            }}
          >
            <i className={`fas fa-${playingState ? "pause" : "play"}`}></i>
          </button>

          <button
            className="route-simulator-reset-btn"
            onClick={reset}
            style={{
              background: "#636366",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
            }}
          >
            <i className="fas fa-redo"></i>
          </button>

          <button
            className="route-simulator-speed-btn"
            onClick={() => changeSpeed(10)}
            style={{
              background: "#3a3a3c",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "17px",
            }}
          >
            <i className="fas fa-plus"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default RouteSimulator;
